<script setup lang="ts">
import { computed, ref, watch, nextTick } from 'vue'
import { useCitations } from '@/composables/useCitations'
import { getRiskColor, getRiskLabel } from '@/lib/riskTier'

const { hover, scheduleClose, cancelClose, dismissHover, focusCitation } = useCitations()

const cardEl = ref<HTMLElement | null>(null)
const pos = ref<{ top: number; left: number }>({ top: 0, left: 0 })

const citation = computed(() => hover.value?.citation ?? null)

// MCP probe references (mcp://) have no real URL; MCP tool results such as
// travel advisories can. Only http(s) sources render as links.
const isLinkable = computed(
  () => !!citation.value?.url && /^https?:\/\//i.test(citation.value.url.trim()),
)

// Distinct per-action tier pairings (only meaningful when >1 distinct tier).
const actionRows = computed(() => {
  const c = citation.value
  if (!c?.actions || c.actions.length < 2) return []
  const distinctTiers = new Set(c.actions.map((a) => a.riskTier))
  if (distinctTiers.size < 2) return []
  return c.actions
})

watch(
  () => hover.value,
  async (h) => {
    if (!h) return
    await nextTick()
    positionCard(h.anchor)
  },
  { immediate: true },
)

function positionCard(anchor: { top: number; bottom: number; left: number; right: number }) {
  const el = cardEl.value
  if (!el) return
  const margin = 8
  const cardRect = el.getBoundingClientRect()
  const vw = window.innerWidth
  const vh = window.innerHeight

  // Prefer below the marker; flip above if it would overflow the viewport.
  let top = anchor.bottom + 6
  if (top + cardRect.height > vh - margin) {
    top = Math.max(margin, anchor.top - cardRect.height - 6)
  }
  let left = anchor.left
  if (left + cardRect.width > vw - margin) {
    left = Math.max(margin, vw - margin - cardRect.width)
  }
  pos.value = { top, left }
}

function onMouseEnter() {
  // Hover bridge: keep the card open while the pointer is over it.
  cancelClose()
}
function onMouseLeave() {
  scheduleClose()
}
function goToReferences() {
  if (citation.value) focusCitation(citation.value.id)
  dismissHover()
}
</script>

<template>
  <Teleport to="body">
    <div
      v-if="citation"
      ref="cardEl"
      class="citation-hovercard"
      role="dialog"
      :aria-label="`Citation ${citation.id} details`"
      :style="{ top: pos.top + 'px', left: pos.left + 'px' }"
      @mouseenter="onMouseEnter"
      @mouseleave="onMouseLeave"
    >
      <div v-if="citation.riskTier != null" class="citation-hovercard__accent" :style="{ backgroundColor: getRiskColor(citation.riskTier) }" />

      <div class="citation-hovercard__body">
        <div class="flex items-center justify-between gap-2">
          <span class="inline-flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wide text-neutral-600">
            <template v-if="citation.riskTier != null">
              <span class="inline-block h-2 w-2 rounded-full" :style="{ backgroundColor: getRiskColor(citation.riskTier) }" aria-hidden="true" />
              {{ getRiskLabel(citation.riskTier) }}
            </template>
          </span>
          <button
            v-if="hover?.pinned"
            type="button"
            class="rounded p-0.5 text-neutral-400 hover:bg-neutral-100 hover:text-neutral-600"
            aria-label="Dismiss citation details"
            @click="dismissHover()"
          >
            <svg class="h-3.5 w-3.5" viewBox="0 0 20 20" fill="none" stroke="currentColor" stroke-width="1.8" aria-hidden="true">
              <path d="M5 5l10 10M15 5L5 15" stroke-linecap="round" />
            </svg>
          </button>
        </div>

        <p class="mt-1.5 text-[13px] font-medium leading-snug text-neutral-800">
          <a v-if="isLinkable" :href="citation.url" target="_blank" rel="noopener noreferrer" class="hover:text-blue-600 hover:underline">
            {{ citation.title }}
          </a>
          <span v-else>{{ citation.title }}</span>
        </p>
        <p
          v-if="!isLinkable && (citation.sourceType === 'mcp' || citation.url.startsWith('mcp://'))"
          class="mt-1 text-[11px] text-neutral-500"
        >
          MCP call · no click-through
        </p>

        <ul v-if="actionRows.length" class="mt-2 space-y-1 border-t border-neutral-100 pt-2">
          <li
            v-for="(a, i) in actionRows"
            :key="i"
            class="flex items-center gap-1.5 text-[11px] text-neutral-500"
          >
            <span class="inline-block h-2 w-2 rounded-full shrink-0" :style="{ backgroundColor: getRiskColor(a.riskTier) }" aria-hidden="true" />
            <span class="font-medium text-neutral-600">{{ getRiskLabel(a.riskTier) }}</span>
            <span v-if="a.title" class="truncate">— {{ a.title }}</span>
          </li>
        </ul>

        <button
          type="button"
          class="mt-2 text-[11px] font-medium text-blue-600 hover:underline"
          @click="goToReferences"
        >
          View in references
        </button>
      </div>
    </div>
  </Teleport>
</template>

<style scoped>
.citation-hovercard {
  position: fixed;
  z-index: 60;
  width: 18rem;
  max-width: calc(100vw - 16px);
  display: flex;
  overflow: hidden;
  border-radius: 10px;
  border: 1px solid #e5e5e5;
  background: #fff;
  box-shadow: 0 8px 28px rgba(0, 0, 0, 0.12);
}
.citation-hovercard__accent {
  width: 4px;
  flex-shrink: 0;
}
.citation-hovercard__body {
  flex: 1;
  padding: 10px 12px;
}
</style>
